/**
 * Tema. Nenhuma cor nasce aqui: tudo vem do cadastro da ticketeira compilada
 * (`src/config/ticketeiras.ts`), inclusive as superfícies. Este arquivo só
 * escolhe entre a paleta de noite e a de dia e dá nomes curtos para as telas.
 *
 * O que fica no `StyleSheet` é geometria — padding, raio, tamanho de fonte,
 * espaçamento. Cor não entra em `StyleSheet.create` porque ela muda com o modo
 * e com o flavor; um estilo estático congelaria a paleta na primeira renderização.
 *
 * As medidas são de portaria: alvo de toque de 56 px, texto grande, número em
 * tabular para o contador não dançar a cada leitura.
 */

import {StyleSheet} from 'react-native';

import {SEMANTICAS, ticketeira, type Ticketeira} from '../config';
import {modoAtual, useModo, type Modo} from './modo';

export type {Modo} from './modo';

/**
 * Famílias por peso em vez de `fontWeight`. No Android a fonte declarada em
 * assets é resolvida pelo nome do arquivo, e `fontFamily: 'Archivo'` com
 * `fontWeight: '800'` cai no regular sintético — que fica borrado no número
 * grande do desfecho.
 */
export const fontes = {
  regular: 'Archivo-Regular',
  medio: 'Archivo-Medium',
  semi: 'Archivo-SemiBold',
  negrito: 'Archivo-Bold',
  extra: 'Archivo-ExtraBold',
  preto: 'Archivo-Black',
  /** Código de barras, CPF, pedido: dígito que o operador compara com o papel. */
  mono: 'JetBrainsMono-Regular',
  monoNegrito: 'JetBrainsMono-Bold',
};

export interface Tema {
  modo: Modo;

  /** Marca. */
  logo: Ticketeira['logo'];
  /** Placa atrás do logo neste modo, ou `null` para logo direto no fundo. */
  placaLogo: string | null;
  empresa: string;
  dominio: string;

  primaria: string;
  tintaPrimaria: string;
  destaque: string;

  fundo: string;
  superficie: string;
  superficieAlta: string;
  borda: string;

  texto: string;
  textoMedio: string;
  textoFraco: string;
  textoApagado: string;

  erro: string;
  erroFundo: string;
  erroBorda: string;
  erroTinta: string;
  aviso: string;
  avisoFundo: string;
  avisoBorda: string;
  avisoTinta: string;
  sucessoFundo: string;
  sucessoTinta: string;
  infoFundo: string;
  infoBorda: string;
  infoTinta: string;

  /** Placas sobre a imagem da câmera — sempre escuras, o fundo é a lente. */
  vidroFundo: string;
  vidroBorda: string;
  vidroTexto: string;
  vidroTextoFraco: string;
  escurecedorMira: string;
  escurecedorModal: string;
}

export function temaDe(empresa: Ticketeira, modo: Modo): Tema {
  const dia = modo === 'dia';
  const s = SEMANTICAS[modo];

  return {
    modo,

    logo: empresa.logo,
    placaLogo: dia ? empresa.placaLogo.dia : empresa.placaLogo.noite,
    empresa: empresa.nome,
    dominio: empresa.dominio,

    primaria: dia ? empresa.corPrimariaDia : empresa.corPrimaria,
    tintaPrimaria: dia ? empresa.corTintaPrimariaDia : empresa.corTintaPrimaria,
    destaque: dia ? empresa.corDestaqueDia : empresa.corDestaque,

    fundo: dia ? empresa.corFundoDia : empresa.corFundo,
    superficie: dia ? empresa.corSuperficieDia : empresa.corSuperficie,
    superficieAlta: dia ? empresa.corSuperficieAltaDia : empresa.corSuperficieAlta,
    borda: dia ? empresa.corBordaDia : empresa.corBorda,

    texto: dia ? empresa.corTextoDia : empresa.corTexto,
    textoMedio: dia ? empresa.corTextoMedioDia : empresa.corTextoMedio,
    textoFraco: dia ? empresa.corTextoFracoDia : empresa.corTextoFraco,
    textoApagado: dia ? empresa.corTextoApagadoDia : empresa.corTextoApagado,

    erro: s.erro,
    erroFundo: s.erroFundo,
    erroBorda: s.erroBorda,
    erroTinta: s.erroTinta,
    aviso: s.aviso,
    avisoFundo: s.avisoFundo,
    avisoBorda: s.avisoBorda,
    avisoTinta: s.avisoTinta,
    sucessoFundo: s.sucessoFundo,
    sucessoTinta: s.sucessoTinta,
    infoFundo: s.infoFundo,
    infoBorda: s.infoBorda,
    infoTinta: s.infoTinta,

    vidroFundo: s.vidroFundo,
    vidroBorda: s.vidroBorda,
    vidroTexto: s.vidroTexto,
    vidroTextoFraco: s.vidroTextoFraco,
    escurecedorMira: s.escurecedorMira,
    escurecedorModal: s.escurecedorModal,
  };
}

const memoria = new Map<string, Tema>();

/** Tema fora de componente (navegação, barra de status). */
export function tema(modo: Modo = modoAtual()): Tema {
  const empresa = ticketeira();
  const chave = `${empresa.id}:${modo}`;
  let pronto = memoria.get(chave);

  if (!pronto) {
    pronto = temaDe(empresa, modo);
    memoria.set(chave, pronto);
  }
  return pronto;
}

/** Tema dentro de componente: re-renderiza sozinho quando o modo muda. */
export function useTema(): Tema {
  return tema(useModo());
}

/** Geometria. Só o que não depende de marca nem de modo. */
/**
 * Largura da coluna no computador.
 *
 * O layout é o do celular; esticado a 1.366 px o teclado do login vira três
 * teclas de 400 px. Vale para as telas (ver App.tsx) e para as folhas e
 * diálogos, que o navegador desenha fora da coluna e esticariam a janela
 * inteira se ninguém segurasse.
 */
export const LARGURA_DA_COLUNA = 560;

export const estilos = StyleSheet.create({
  tela: {
    flex: 1,
  },
  conteudo: {
    padding: 20,
    gap: 14,
  },
  cabecalho: {
    height: 60,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
  },
  tituloCabecalho: {
    fontSize: 17,
    fontFamily: fontes.extra,
    letterSpacing: -0.3,
  },
  legendaCabecalho: {
    fontSize: 12,
    fontFamily: fontes.medio,
  },
  titulo: {
    fontSize: 30,
    fontFamily: fontes.extra,
    letterSpacing: -0.8,
    lineHeight: 34,
  },
  subtitulo: {
    fontSize: 15,
    fontFamily: fontes.regular,
    lineHeight: 22,
  },
  rotulo: {
    fontSize: 12,
    fontFamily: fontes.negrito,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  campo: {
    borderWidth: 1,
    borderRadius: 14,
    paddingHorizontal: 18,
    paddingVertical: 16,
    fontSize: 20,
    fontFamily: fontes.semi,
  },
  botao: {
    height: 60,
    borderRadius: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  textoBotao: {
    fontSize: 18,
    fontFamily: fontes.extra,
    letterSpacing: -0.2,
  },
  botaoSecundario: {
    height: 56,
    borderRadius: 15,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
  },
  textoBotaoSecundario: {
    fontSize: 16,
    fontFamily: fontes.negrito,
  },
  cartao: {
    borderRadius: 18,
    borderWidth: 1,
    padding: 18,
    gap: 6,
  },
  linha: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  /** Ícone quadrado com moldura — o alvo de toque de 44 px do cabeçalho. */
  botaoIcone: {
    width: 44,
    height: 44,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  numero: {
    fontFamily: fontes.extra,
    fontVariant: ['tabular-nums'],
  },
  mono: {
    fontFamily: fontes.mono,
    letterSpacing: 0.4,
  },
  erro: {
    fontSize: 15,
    fontFamily: fontes.medio,
  },
});
